import { useCallback } from "react";

export function useBulkTaskActions({
  selectedTaskObjects,
  currentUser,
  employees,
  updateProject,
  setToastMessage,
  setToastType,
  activeProjectId,
  deleteProject,
  setSelectedTasks,
  setShowStatusDropdown,
  nowIST,
}) {
  const handleBulkPriorityUpdate = useCallback(
    async (newPriority) => {
      if (!selectedTaskObjects.length) {
        console.log("No tasks selected. Exiting function.");
        return;
      }

      const currentUserName =
        currentUser?.fullName ||
        currentUser?.firstName ||
        currentUser?.username ||
        "Unknown";

      const createdByTask = {
        userId: currentUser?.userId || "Unknown",
        fullName:
          currentUser?.fullName ||
          currentUser?.firstName ||
          currentUser?.username ||
          "Unknown",
        email: currentUser?.email || "info@hubsyntax.com",
      };

      try {
        await Promise.all(
          selectedTaskObjects.map((task) => {
            let comments = [];
            if (newPriority) {
              comments.push({
                text: `${currentUserName} changed priority to: ${newPriority}`,
                createdBy: currentUserName,
                createdAt: nowIST,
              });
            }

            const payload = {
              id: task.projectId,
              subProjectId: task.subProjectId || null,
              taskId: task._id,
              status: task.status,
              priority: newPriority,
              comments,
              createdByTask,
            };

            console.log("Bulk Priority Update Payload for Task:", payload);

            return updateProject.mutateAsync(payload);
          }),
        );

        setToastMessage("Priority updated successfully");
        setToastType("success");
      } catch (err) {
        console.error("Bulk priority update failed:", err);
        setToastMessage("Error updating priority");
        setToastType("error");
      }
    },
    [selectedTaskObjects, currentUser, updateProject],
  );

  // assign task update  status//
  const handleBulkStatusUpdate = useCallback(
    async (newStatus) => {
      if (!selectedTaskObjects.length) return;

      const currentUserName =
        currentUser?.fullName ||
        currentUser?.firstName ||
        currentUser?.username ||
        "Unknown";
      const createdByTask = {
        userId: currentUser?.userId || "Unknown",
        fullName:
          currentUser?.fullName ||
          currentUser?.firstName ||
          currentUser?.username ||
          "Unknown",
        email: currentUser?.email || "info@hubsyntax.com",
      };

      try {
        const tasksToUpdate = selectedTaskObjects.filter(
          (task) => task.status !== newStatus,
        );

        await Promise.all(
          tasksToUpdate.map((task) => {
            const newComment = {
              text: `${currentUserName} changed status from ${task.status} to ${newStatus}`,
              createdBy: currentUserName,
              createdAt: nowIST,
            };

            return updateProject.mutateAsync({
              id: task.projectId,
              taskId: task._id,
              status: newStatus,
              comments: [newComment],
              subProjectId: task.subProjectId || null,
              createdByTask: createdByTask,
            });
          }),
        );

        setSelectedTasks({});
        setToastMessage("Status updated for selected tasks");
        setToastType("success");
        setShowStatusDropdown(false);
      } catch (err) {
        console.error("Bulk status update failed:", err);
        setToastMessage("Error updating status");
        setToastType("error");
      }
    },
    [selectedTaskObjects, currentUser, updateProject],
  );

  // bulk assign task update //
  const handleBulkAssigneeUpdate = useCallback(
    async (newAssignees) => {
      if (!selectedTaskObjects.length) {
        console.log("No tasks selected. Exiting function.");
        return;
      }

      const currentUserName =
        currentUser?.fullName ||
        currentUser?.firstName ||
        currentUser?.username ||
        "Unknown";
      const createdByTask = {
        userId: currentUser?.userId || "Unknown",
        fullName:
          currentUser?.fullName ||
          currentUser?.firstName ||
          currentUser?.username ||
          "Unknown",
        email: currentUser?.email || "info@hubsyntax.com",
      };

      const getEmployeeInfo = (id) => {
        return employees.find((e) => e.employeeId === id);
      };

      try {
        await Promise.all(
          selectedTaskObjects.map((task) => {
            let comments = [];

            if (newAssignees.length > 0) {
              const assignedNames = newAssignees
                .map((id) => getEmployeeInfo(id)?.firstName || "Unknown")
                .join(", ");

              comments.push({
                text: `${currentUserName} assigned to: ${assignedNames}`,
                createdBy: currentUserName,
                createdAt: nowIST,
              });
            }

            const formattedAssignees = newAssignees
              .map((id) => getEmployeeInfo(id))
              .filter(Boolean);

            const payload = {
              id: task.projectId,
              subProjectId: task.subProjectId || null,
              taskId: task._id,
              status: task.status,
              assignedEmployees: formattedAssignees,
              comments,
              createdByTask,
            };
            console.log("Bulk Update Payload for Task:", payload);

            return updateProject.mutateAsync(payload);
          }),
        );

        setToastMessage("Assignees updated successfully");
        setToastType("success");
      } catch (err) {
        console.error("Bulk assignee update failed:", err);
        setToastMessage("Error updating assignees");
        setToastType("error");
      }
    },
    [selectedTaskObjects, currentUser, employees, updateProject],
  );

  // bulk date update  functions//
  const handleBulkDateUpdate = useCallback(
    async (date) => {
      if (!selectedTaskObjects.length) return;

      const currentUserName =
        currentUser?.fullName ||
        currentUser?.firstName ||
        currentUser?.username ||
        "Unknown";

      const createdByTask = {
        userId: currentUser?.userId || "Unknown",
        fullName: currentUserName,
        email: currentUser?.email || "info@hubsyntax.com",
      };

      const formatMySQLDate = (date) => {
        const d = new Date(date);

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");

        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const seconds = "00";

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      try {
        let updatedTasks = 0;

        const tasksToUpdate = selectedTaskObjects.filter(
          (task) => task.projectId && task._id,
        );

        const formattedDate = date ? date : "cleared";

        await Promise.all(
          tasksToUpdate.map((task) => {
            const newComment = {
              text: date
                ? `${currentUserName} set the due date to ${formattedDate}`
                : `${currentUserName} cleared the due date`,
              createdBy: currentUserName,
              createdAt: nowIST,
            };

            return updateProject.mutateAsync({
              id: task.projectId,
              subProjectId: task.subProjectId || null,
              taskId: task._id,
              status: task.status,
              dueDate: date ? formatMySQLDate(date) : null,
              comments: [newComment],
              createdByTask,
            });
          }),
        );

        updatedTasks = tasksToUpdate.length;

        if (updatedTasks > 0) {
          setToastMessage(
            date
              ? "Due date updated for selected tasks"
              : "Due date cleared for selected tasks",
          );
          setToastType("success");
        } else {
          setToastMessage("No tasks were updated");
          setToastType("warning");
        }
      } catch (err) {
        console.error("Bulk date update failed:", err);
        setToastMessage("Error updating due dates");
        setToastType("error");
      }
    },
    [selectedTaskObjects, currentUser, updateProject],
  );

  // copy task //
  const handleCopyTasks = async () => {
    if (!selectedTaskObjects.length) return;

    //  Freeze selection
    const tasksToCopy = [...selectedTaskObjects];

    const createdByTask = {
      userId: currentUser?.userId || "Unknown",
      fullName:
        currentUser?.fullName ||
        currentUser?.firstName ||
        currentUser?.username ||
        "Unknown",
      email: currentUser?.email || "unknown@hubsyntax.com",
    };

    try {
      for (let i = 0; i < tasksToCopy.length; i++) {
        const task = tasksToCopy[i];

        if (!task.projectId) {
          console.error("Missing projectId", task);
          continue;
        }

        await updateProject.mutateAsync({
          id: task.projectId,
          subProjectId: task.subProjectId || null,
          taskId: undefined,
          taskTitle: `${task.title} (Copy ${i + 1})`,
          description: task.description,
          assignedEmployees:
            task.assignedEmployees
              ?.map((e) => e.userId || e.employeeId || e._id)
              .filter(Boolean) || [],
          status: task.status,
          priority: task.priority,
          dueDate: task.dueDate,
          comments: [],
          createdByTask,
        });
      }

      setToastMessage("Tasks copied successfully!");
      setToastType("success");
    } catch (err) {
      console.error("Copy failed:", err);
      setToastMessage("Error copying tasks");
      setToastType("error");
    }
  };

  // delete task //
  const handleDeleteTasks = useCallback(async () => {
    if (!selectedTaskObjects.length) return;

    setToastMessage("Deleting selected tasks...");
    setToastType("info");

    const tasksToDelete = [...selectedTaskObjects];
    setSelectedTasks({});

    try {
      await Promise.all(
        tasksToDelete.map((task) =>
          deleteProject.mutateAsync({
            projectId: activeProjectId,
            taskId: task.id || task._id,
            subProjectId: task.subProjectId || null,
          }),
        ),
      );

      setToastMessage("Selected tasks deleted successfully!");
      setToastType("success");
    } catch (err) {
      console.error("Delete failed:", err);

      setToastMessage(err?.response?.data?.message || "Error deleting tasks");
      setToastType("error");
    }
  }, [selectedTaskObjects, deleteProject, activeProjectId]);

  return {
    handleBulkPriorityUpdate,
    handleBulkStatusUpdate,
    handleBulkAssigneeUpdate,
    handleBulkDateUpdate,
    handleCopyTasks,
    handleDeleteTasks,
  };
}
